import { AdminUser } from "@/constants/types";
import MuiButton from "../../components/basicComponent/MuiButton";
import { MuiDropDown } from "../../components/basicComponent/MuiDropDown";
import { MuiInput } from "../../components/basicComponent/MuiInput";
import Header from "../../sharedScreens/Header";
import SideMenu from "../../sharedScreens/menuItems/SideMenu";
import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { AdminUservalidationSchema } from "../../validations/Schema";
import {
  titleList,
  roleList,
  statusList,
  superAdminRole,
} from "../../constants/constant";
import { UtilService } from "../../services/shared/UtilService";
import { msgTypes } from "../../constants/msgTypes";
import { useSweetAlert } from "../../services/customHooks/useSweetAlert";
import Permissions from "./Permissions";
import { Tabs, Tab, Pagination, Switch } from "@mui/material";
import ActivityIndicator from "../../components/basicComponent/ActivityIndicator";
import NoDataFlightCard from "../../components/composite/NoDataFlightCard";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getChangedFields } from "../../constants/helper";
import PhoneInput from "react-phone-input-2";
import { number } from "yup";

const AdminUserManagement = () => {
  const [originalData, setOriginalData] = useState<AdminUser | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [userNameList, setUserNameList] = useState<[]>([]);
  const [filterUserName, setFilterUserName] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [clearPermissions, setClearPermissions] = useState<boolean>();

  // Refs for form fields to enable scrolling and focusing
  const fieldRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  const initialValues: AdminUser = {
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    role: 5,
    password: "",
    confirmPassword: "",
    feature_ids: [],
  };

  const { showSuccess, showError, showInfo, showConfirm } = useSweetAlert();
  const [tabValue, setTabValue] = useState(0);
  const [selectedUserName, setSelectedUserName] = useState<string>("");

  // Function to scroll to the first error field and focus on it
  const scrollToFirstError = (errors: Record<string, any>) => {
    const fieldOrder = [
      "title",
      "firstName",
      "lastName",
      "email",
      "mobile",
      "role",
      "password",
      "confirmPassword",
      "feature_ids",
    ];

    const firstErrorField = fieldOrder.find((field) => errors[field]);

    if (firstErrorField) {
      const element = fieldRefs.current[firstErrorField];

      // ✅ Check if element is not null
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        setTimeout(() => {
          const inputElement = element.querySelector(
            "input, select, textarea"
          ) as HTMLElement | null;

          if (inputElement) {
            inputElement.focus();
          } else {
            element.focus(); // Also safe now
          }
        }, 300);
      }
    }
  };

  // Function to register field refs
  const setFieldRef = (fieldName: string) => (ref: HTMLElement | null) => {
    fieldRefs.current[fieldName] = ref;
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: AdminUservalidationSchema,
    onSubmit: async (values) => {
      console.log("✅ Form Submitted:", values);

      try {
        const payload = {
          email: values.email,
          first_name: values.firstName,
          last_name: values.lastName,
          password: values.password,
          confirm_password: values.confirmPassword,
          role: values.role,
          feature_ids: values.feature_ids,
          phoneNumber: values.mobile,
          title: values.title,
        };
        const response = await UtilService.AdminUser(payload);
        if (response.statusMsg === msgTypes.SUCCESS.msg) {
          showSuccess("Success!", "User has been created successfully.", {
            timer: 3000,
            timerProgressBar: true,
          }).then(() => {
            formik.resetForm();
            setTabValue(1);
            const status = true;
            getSuperAdminUSer(status);
          });
        }
      } catch (error) {
        console.error("Error creating user:", error);
      }
    },
    validate: (values) => {
      try {
        // ✅ Only pass context here to Yup
        AdminUservalidationSchema.validateSync(values, {
          abortEarly: false,
          context: { isEditMode: !!originalData }, // ✅ context used properly here
        });
        return {};
      } catch (error: any) {
        const errors: Record<string, string> = {};
        error.inner.forEach((err: any) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });
        return errors;
      }
    },
  });

  useEffect(() => {
    if (originalData) {
      formik.setValues({
        title: originalData?.title ?? formik.values.title,
        firstName: originalData?.firstName ?? formik.values.firstName,
        lastName: originalData?.lastName ?? formik.values.lastName,
        email: originalData?.email ?? formik.values.email,
        mobile: originalData?.phoneNumber ?? formik.values.mobile,
        role: originalData?.role ?? 5,
        feature_ids:
          originalData?.featurePermissions?.map((i: any) => i?.feature) ?? [],
      });
    }
  }, [originalData]);

  const getPermissionsId = (ids: string[]) => {
    formik.setFieldValue("feature_ids", ids);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    sessionStorage.setItem("adminUserTab", newValue.toString());
  };

  const getSuperAdminUSer = async (status?: boolean) => {
    setLoading(true);
    const param = {
      roleName: "sub_admin",
      status: status,
    };

    try {
      const response = await UtilService.getSuperAdminUser(param);
      if (response?.statusMsg === msgTypes.SUCCESS.msg) {
        const data = response?.data?.map((i: any) => ({
          label: `${i?.firstName ?? ""} ${i?.lastName ?? ""}`,
          value: `${i?.firstName ?? ""} ${i?.lastName ?? ""}`,
        }));
        setUserNameList(data);
        setUsers(response?.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching users:", error);
    }
  };

  const fetchFilteredUsers = async () => {
    const isNameSelected = filterUserName?.trim() !== "";
    const isMobileSelected = filterMobile?.trim() !== "";
    const isStatusSelected = filterStatus?.trim() !== "";

    if (!isNameSelected && !isMobileSelected && !isStatusSelected) {
      showInfo(
        "Validation",
        "Please select at least one filter: User Name, Mobile or Status."
      );
      return;
    }
    const payload = {
      fullName: filterUserName || null,
      mobileNo: filterMobile || null,
      status:
        filterStatus === "active"
          ? true
          : filterStatus === "inactive"
          ? false
          : null,
      roleName: "sub_admin",
    };

    try {
      const response = await UtilService.getSuperAdminUser(payload);
      if (response?.statusMsg === msgTypes.SUCCESS.msg) {
        setUsers(response?.data);

        const dropdownData = response?.data?.map((i: any) => ({
          label: `${i?.firstName ?? ""} ${i?.lastName ?? ""}`,
          value: `${i?.firstName ?? ""} ${i?.lastName ?? ""}`,
        }));
        setUserNameList(dropdownData);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Filtering failed:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filterUserName, filterMobile, filterStatus]);

  const editSuperAdminUser = async () => {
    const changedFields = getChangedFields(originalData ?? {}, formik.values);

    if (Object.keys(changedFields).length === 0) {
      showInfo("No changes", "No fields were modified.");
      return;
    }
    const payload = {
      ...(changedFields.email && {
        email: changedFields.email,
      }),
      ...(changedFields.feature_ids && {
        feature_ids: changedFields.feature_ids,
      }),
      ...(changedFields.firstName && { firstName: changedFields.firstName }),
      ...(changedFields.lastName && { lastName: changedFields.lastName }),
      ...(changedFields.mobile && {
        phoneNumber: changedFields.mobile,
      }),
      ...(changedFields.role && {
        role: changedFields.role,
      }),
      ...(changedFields.title && {
        title: changedFields.title,
      }),
    };
    const id = originalData?.id || "";
    try {
      const response = await UtilService.EditSuperAdminUser(payload, id);
      if (response.statusMsg === msgTypes.SUCCESS.msg) {
        showSuccess("Success!", "User has been updated successfully.", {
          timer: 3000,
          timerProgressBar: true,
        }).then(() => {
          setTabValue(1);
          const status = true;
          getSuperAdminUSer(status);
          setOriginalData(null);
          formik.resetForm();
        });
      }
    } catch (error) {
      showError("Error!", "Failed to update user.");
      console.error(error);
    }
  };

  console.error("formikerror", formik.errors);
  const handleClearClick = () => {
    if (!filterUserName && !filterMobile && !filterStatus) {
      showInfo("Nothing to Clear", "All filters are already empty.");
      return;
    }
    setFilterUserName("");
    setFilterMobile("");
    setFilterStatus("");
    setCurrentPage(1);
    const status = true;
    getSuperAdminUSer(status);
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const paginatedUsers = users.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const setInActiveCoupon = async (payload: {}, id: string) => {
    const response = await UtilService.EditSuperAdminUser(payload, id);
    if (response.statusMsg === msgTypes.SUCCESS.msg) {
      const status = true;
      getSuperAdminUSer(status);
    }
  };

  const handleToggleBtn = (item: AdminUser) => {
    if (item.isActive) {
      showConfirm(
        "",
        `Are you sure you want to deactivate ${item.firstName} ${item?.lastName} ? `
      ).then((result) => {
        if (result?.isConfirmed) {
          const payload = {
            isActive: false,
          };
          setInActiveCoupon(payload, item?.id ?? "");
        }
      });
    } else {
      showConfirm(
        "",
        `Are you sure you want to activate ${item.firstName} ${item?.lastName} ? `
      ).then((result) => {
        if (result?.isConfirmed) {
          const payload = {
            isActive: true,
          };
          setInActiveCoupon(payload, item?.id ?? "");
        }
      });
    }
  };

  // Enhanced submit handler with scroll to error functionality
  const handleFormSubmit = async () => {
    // Trigger form validation
    const errors = await formik.validateForm();
    formik.setTouched({
      title: true,
      firstName: true,
      lastName: true,
      email: true,
      mobile: true,
      role: true,
      password: !originalData,
      confirmPassword: !originalData,
      feature_ids: true,
    });

    if (Object.keys(errors).length > 0) {
      // If there are errors, scroll to the first error field
      scrollToFirstError(errors);
      return;
    }

    // If no errors, proceed with form submission
    if (originalData) {
      editSuperAdminUser();
    } else {
      formik.handleSubmit();
    }
  };
  useEffect(() => {
    const savedTab = sessionStorage.getItem("adminUserTab");
    if (savedTab !== null) {
      setTabValue(Number(savedTab));
    }

    const status = true;
    getSuperAdminUSer(status);
  }, []);

  return (
    <>
      <div className="grow">
        <SideMenu />
        <div className="wrapper flex grow flex-col">
          <Header />
          <main className="grow content" id="content" role="content">
            <div className="container-fixed">
              <div className="card-header text-center">
                <h3 className="card-title">Admin User Management</h3>
              </div>
              <div className="row">
                <div className="card card-custom ancillary-services-card">
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="user-management-tabs"
                    variant="scrollable"
                    scrollButtons="auto" // show only if overflow
                    allowScrollButtonsMobile // show on mobile
                  >
                    <Tab
                      label="Create User"
                      icon={<AccountCircleIcon />}
                      iconPosition="start"
                    />
                    <Tab
                      label="View Users"
                      icon={<VisibilityIcon />}
                      iconPosition="start"
                    />
                  </Tabs>

                  {tabValue === 0 && (
                    <div className="card-body">
                      <div className="row mt-4">
                        <div
                          className="col-md-4 mb-md-0 mb-3 "
                          ref={setFieldRef("title")}
                        >
                          <MuiDropDown
                            label="Title"
                            placeholder={"Title"}
                            options={titleList}
                            value={formik.values.title}
                            onChange={(value) =>
                              formik.setFieldValue("title", value)
                            }
                            isRequired={true}
                            isInvalid={
                              formik.touched.title &&
                              Boolean(formik.errors.title)
                            }
                            errorText={
                              formik.touched.title && formik.errors.title
                                ? formik.errors.title
                                : ""
                            }
                          />
                        </div>
                        <div
                          className="col-md-4 mb-md-0 mb-3 my-under-500"
                          ref={setFieldRef("firstName")}
                        >
                          <MuiInput
                            label={"First Name"}
                            placeholder="First Name"
                            name="firstName"
                            isRequired={true}
                            value={formik.values.firstName}
                            onChange={(e: any) =>
                              formik.setFieldValue("firstName", e)
                            }
                            error={
                              formik.touched.firstName &&
                              !!formik.errors.firstName
                            }
                            helperText={
                              formik.touched.firstName
                                ? formik.errors.firstName
                                : ""
                            }
                            onBlur={formik.handleBlur}
                          />
                        </div>
                        <div
                          className="col-md-4 my-under-500"
                          ref={setFieldRef("lastName")}
                        >
                          <MuiInput
                            label={"Last Name"}
                            placeholder="Last Name"
                            name="lastName"
                            isRequired={true}
                            value={formik.values.lastName}
                            onChange={(e: any) =>
                              formik.setFieldValue("lastName", e)
                            }
                            error={
                              formik.touched.lastName &&
                              !!formik.errors.lastName
                            }
                            helperText={
                              formik.touched.lastName
                                ? formik.errors.lastName
                                : ""
                            }
                            onBlur={formik.handleBlur}
                          />
                        </div>
                        <div
                          className="col-md-4 mt-4 "
                          ref={setFieldRef("email")}
                        >
                          <MuiInput
                            label={"Email"}
                            placeholder="Email"
                            name="email"
                            isRequired={true}
                            value={formik.values.email}
                            onChange={(e: any) =>
                              formik.setFieldValue("email", e)
                            }
                            error={
                              formik.touched.email && !!formik.errors.email
                            }
                            helperText={
                              formik.touched.email ? formik.errors.email : ""
                            }
                            onBlur={formik.handleBlur}
                          />
                        </div>
                        <div
                          className="col-md-4 mt-4"
                          ref={setFieldRef("mobile")}
                        >
                          {/* <MuiInput
                            label={"Mobile"}
                            placeholder="Enter Mobile"
                            name="mobile"
                            isRequired={true}
                            value={formik.values.mobile}
                            onChange={(value) => {
                              const digitsOnly = value
                                .replace(/\D/g, "")
                                .slice(0, 10);
                              formik.setFieldValue("mobile", digitsOnly);
                            }}
                            onBlur={() => formik.setFieldTouched("mobile")}
                            error={
                              formik.touched.mobile && !!formik.errors.mobile
                            }
                            helperText={
                              formik.touched.mobile ? formik.errors.mobile : ""
                            }
                          /> */}
                          <div className="form-group">
                            <PhoneInput
                              value={filterMobile}
                              placeholder="Enter mobile number"
                              onChange={(value) => {
                                const digitsOnly = value
                                  .replace(/\D/g, "")
                                  .slice(0, 10);
                                formik.setFieldValue("mobile", digitsOnly);
                              }}
                              inputStyle={{
                                width: "100%",
                                borderColor:
                                  formik.touched.mobile && formik.errors.mobile
                                    ? "#d32f2f"
                                    : "#ccc",
                              }}
                              preferredCountries={["ae", "in", "us"]}
                              enableSearch
                              inputProps={{
                                name: "mobile",
                                required: true,
                                onBlur: () =>
                                  formik.setFieldTouched("mobile", true),
                              }}
                              isValid={(value: string) => {
                                const mobileRegex = /^[0-9]{8,15}$/;
                                const isValid = mobileRegex.test(
                                  value.replace(/^\+?\d{1,4}/, "")
                                );
                                return isValid || "";
                              }}
                            />

                            {/* Manually show Formik error below the input */}
                            {formik.touched.mobile && formik.errors.mobile && (
                              <span className="phone-error-msg">
                                {formik.errors.mobile}
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          className="col-md-4 mt-4"
                          ref={setFieldRef("role")}
                        >
                          <MuiDropDown
                            placeholder={"Role"}
                            options={superAdminRole}
                            value={formik.values.role?.toString()}
                            onChange={(value) =>
                              formik.setFieldValue("role", Number(value))
                            }
                          />
                        </div>
                        {!originalData && (
                          <div
                            className="col-md-4 mt-4"
                            ref={setFieldRef("password")}
                          >
                            <MuiInput
                              label={"Password"}
                              placeholder="Password"
                              name="password"
                              isRequired={true}
                              value={formik.values.password}
                              onChange={(e: any) =>
                                formik.setFieldValue("password", e)
                              }
                              error={
                                formik.touched.password &&
                                !!formik.errors.password
                              }
                              helperText={
                                formik.touched.password
                                  ? formik.errors.password
                                  : ""
                              }
                              onBlur={formik.handleBlur}
                            />
                          </div>
                        )}
                        {!originalData && (
                          <div
                            className="col-md-4 mt-4"
                            ref={setFieldRef("confirmPassword")}
                          >
                            <MuiInput
                              label={"Confirm Password"}
                              placeholder="Confirm Password"
                              name="confirmPassword"
                              isRequired={true}
                              value={formik.values.confirmPassword}
                              onChange={(e: any) =>
                                formik.setFieldValue("confirmPassword", e)
                              }
                              error={
                                formik.touched.confirmPassword &&
                                !!formik.errors.confirmPassword
                              }
                              helperText={
                                formik.touched.confirmPassword
                                  ? formik.errors.confirmPassword
                                  : ""
                              }
                              onBlur={formik.handleBlur}
                            />
                          </div>
                        )}
                        <div ref={setFieldRef("feature_ids")}>
                          <Permissions
                            permissionId={getPermissionsId}
                            selectedPermissionId={
                              !!originalData
                                ? originalData?.featurePermissions?.map(
                                    (i: any) => i?.feature
                                  )
                                : formik.values.feature_ids
                            }
                            clearPermissions={clearPermissions}
                          />
                        </div>

                        <div className="row">
                          <div
                            className={`${
                              !!originalData ? "col-md-8" : "col-12"
                            }`}
                          >
                            <div
                              className={`user-button mt-3 ${
                                !!originalData
                                  ? "justify-content-start"
                                  : "justify-content-center"
                              }`}
                            >
                              {/* <MuiButton
                            className="clear-btn"
                            label={"Cancel"}
                            type="button"
                            onClick={() => {
                              setValue(1);
                            }}
                          /> */}

                              <MuiButton
                                className="clear-btn"
                                label={originalData ? "Create New" : "Clear"}
                                onClick={
                                  !!originalData
                                    ? () => {
                                        setOriginalData(null);
                                        formik.resetForm();
                                        setClearPermissions(!clearPermissions);
                                      }
                                    : () => {
                                        formik.resetForm();
                                        setClearPermissions(!clearPermissions);
                                      }
                                }
                              />
                              <MuiButton
                                className="main-btn get-btn"
                                variant="contained"
                                size="large"
                                label={!!originalData ? "Update" : "Submit"}
                                onClick={handleFormSubmit}
                              />
                            </div>
                          </div>
                          {!!originalData && (
                            <div className="col-md-4 mt-3 d-flex justify-content-end align-items-center">
                              <button
                                onClick={() => {
                                  setTabValue(1);
                                  sessionStorage.setItem("adminUserTab", "1");
                                }}
                                type="button"
                                className="btn btn-danger d-flex align-items-center gap-2"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {tabValue === 1 && (
                    <div className="row">
                      <div className="col-md-12">
                        <div className="card card-custom">
                          <div className="card-body">
                            <div className="row  align-items-end mb-3">
                              <div className="col-md-3 mb-2 user-manage-model">
                                <MuiDropDown
                                  options={userNameList}
                                  placeholder="User Name"
                                  value={filterUserName}
                                  onChange={(value: string) =>
                                    setFilterUserName(value)
                                  }
                                />
                              </div>

                              <div className="col-md-3 mb-2 user-manage-model">
                                <MuiInput
                                  placeholder="Mobile Number"
                                  label="Mobile Number"
                                  value={filterMobile}
                                  onChange={(value: string) =>
                                    setFilterMobile(value)
                                  }
                                />
                              </div>

                              <div className="col-md-3 mb-2 user-manage-model">
                                <MuiDropDown
                                  placeholder="Status"
                                  options={statusList}
                                  value={filterStatus}
                                  onChange={(value: string) =>
                                    setFilterStatus(value)
                                  }
                                />
                              </div>

                              <div className="col-md-3 user-button mb-2 d-flex gap-2">
                                <MuiButton
                                  className="get-btn"
                                  label="Get"
                                  type="button"
                                  onClick={() => {
                                    fetchFilteredUsers();
                                  }}
                                  style={{ width: "-webkit-fill-available" }}
                                />
                                <MuiButton
                                  className="clear-btn"
                                  label="Clear"
                                  type="button"
                                  onClick={handleClearClick}
                                  style={{ width: "-webkit-fill-available" }}
                                />
                              </div>
                            </div>
                            <div className="scrollable-x-auto mb-2">
                              <table className="table table-bordered table-hover align-middle">
                                <thead>
                                  <tr>
                                    <th>Title</th>
                                    <th>First Name</th>
                                    <th>Last Name</th>
                                    <th>Email</th>
                                    <th>Mobile</th>
                                    <th>Status</th>
                                    <th>Edit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {loading ? (
                                    <tr>
                                      <td colSpan={12}>
                                        <div className="d-flex justify-content-center align-items-center py-4">
                                          <ActivityIndicator />
                                        </div>
                                      </td>
                                    </tr>
                                  ) : paginatedUsers.length > 0 ? (
                                    paginatedUsers?.map((user, index) => (
                                      <tr key={index}>
                                        <td className="text-capitalize">
                                          <div className="sort">
                                            {user.title}
                                          </div>
                                        </td>
                                        <td className="text-capitalize">
                                          <div className="sort">
                                            {user.firstName}
                                          </div>
                                        </td>
                                        <td className="text-capitalize">
                                          <div className="sort">
                                            {user.lastName}
                                          </div>
                                        </td>
                                        <td>
                                          <div className="sort">
                                            {user.email}
                                          </div>
                                        </td>
                                        <td>
                                          <div className="sort">
                                            {user.phoneNumber}
                                          </div>
                                        </td>
                                        <td>
                                          <Switch
                                            checked={user.isActive}
                                            onClick={() =>
                                              handleToggleBtn(user)
                                            }
                                            size="small"
                                          />
                                        </td>
                                        <td className="position-sticky start-0  z-2">
                                          <span
                                            className="text-success "
                                            onClick={() => {
                                              setTabValue(0);
                                              sessionStorage.setItem(
                                                "adminUserTab",
                                                "0"
                                              );
                                              setOriginalData({
                                                ...user,
                                              });
                                            }}
                                          >
                                            <span className="badge badge-sm badge-warning badge-outline cursor-pointer">
                                              <i className="fa-solid fa-pen-to-square pe-0"></i>
                                            </span>
                                          </span>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan={8}>
                                        <NoDataFlightCard headingText="No Data Found" />
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            <Pagination
                              count={Math.ceil(users.length / rowsPerPage)}
                              page={currentPage}
                              onChange={handleChangePage}
                              color="primary"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default AdminUserManagement;
